import axios from "axios";
import { ERROR_CODE_MAP } from "@/utils/errorMapper";

export function resolveApiError(
  backendCode: string | undefined,
  t: (key: string) => string,
  fallbackKey = 'errors.global'
): string {
  if (!backendCode) {
    return t(fallbackKey);
  }
  
  const translationKey = ERROR_CODE_MAP[backendCode];
  if (!translationKey) {
    return t(fallbackKey);
  }
  return t(translationKey);
}

const CODE_FIELDS = [
  'hydra:description',
  'description',
  'error',
  'code',
  'detail',
] as const;

const MESSAGE_FIELDS = ['message', 'detail'] as const;

const ERROR_CODE_PATTERN = /^[A-Z_]+$/;

const isErrorCode = (value: unknown): value is string =>
  typeof value === 'string' && ERROR_CODE_PATTERN.test(value);

export function extractApiErrorCode(error: unknown): string | undefined {
  if (!axios.isAxiosError(error) || !error.response?.data) {
    return undefined;
  }

  const data = error.response.data as Record<string, unknown>;

  for (const field of CODE_FIELDS) {
    if (isErrorCode(data[field])) {
      return data[field] as string;
    }
  }

  // Validation errors (422) carry their code as the message of a violation.
  const violations = data.violations;
  if (Array.isArray(violations)) {
    const message = (violations[0] as Record<string, unknown> | undefined)?.message;
    if (isErrorCode(message)) {
      return message;
    }
  }

  return undefined;
}

export function resolveApiErrorFromError(
  error: unknown,
  t: (key: string) => string,
  fallbackKey = 'errors.global'
): string {
  const code = extractApiErrorCode(error);
  if (code) {
    return resolveApiError(code, t, fallbackKey);
  }

  if (axios.isAxiosError(error) && error.response?.data) {
    const data = error.response.data as Record<string, unknown>;

    for (const field of MESSAGE_FIELDS) {
      const value = data[field];
      if (typeof value === 'string' && value.length > 0) {
        return value;
      }
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return t(fallbackKey);
}
